import { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Check, ArrowUpRight } from "lucide-react";
import { Eyebrow } from "./Reveal";
import { profile } from "../../data/resume";

export const Contact = () => {
  const [copied, setCopied] = useState(false);

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(profile.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      setCopied(false);
    }
  };

  const details = [
    { label: "Phone", value: profile.phone, href: `tel:${profile.phoneHref}`, testid: "contact-phone-link" },
    { label: "LinkedIn", value: "Contact on LinkedIn", href: profile.linkedin, testid: "contact-linkedin-link", external: true },
    { label: "Location", value: profile.location, href: null, testid: "contact-location" },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-t border-hair bg-paper grain-overlay overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-6 sm:px-12 lg:px-20 py-24 lg:py-40">
        <Eyebrow>(Contact)</Eyebrow>

        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-6 font-serif font-normal text-5xl sm:text-7xl lg:text-[8rem] leading-[0.95] text-ink"
        >
          Let's talk<span className="text-rust">.</span>
        </motion.h2>

        <p className="mt-8 max-w-xl font-sans text-lg leading-relaxed text-inksoft">
          {profile.availability}. I'd love to hear about the product you're
          building — reach out anytime.
        </p>

        <div className="mt-12 flex flex-wrap items-center gap-4">
          <a
            data-testid="contact-email-link"
            href={`mailto:${profile.email}`}
            className="group inline-flex items-center gap-3 rounded-full bg-ink px-7 py-4 font-sans text-base text-paper transition-colors duration-300 hover:bg-rust"
          >
            {profile.email}
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <button
            data-testid="copy-email-button"
            onClick={copyEmail}
            className="inline-flex items-center gap-2 rounded-full border border-ink px-6 py-4 font-sans text-sm text-ink transition-colors duration-300 hover:bg-ink hover:text-paper"
          >
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            {copied ? "Copied" : "Copy email"}
          </button>
        </div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 border-t border-hair">
          {details.map((d) => (
            <div
              key={d.label}
              className="border-b sm:border-b-0 sm:border-r last:border-r-0 border-hair py-8 sm:pr-8 sm:pl-0 first:pl-0"
            >
              <span className="font-mono text-xs uppercase tracking-[0.22em] text-inkmuted">
                {d.label}
              </span>
              {d.href ? (
                <a
                  data-testid={d.testid}
                  href={d.href}
                  target={d.external ? "_blank" : undefined}
                  rel={d.external ? "noopener noreferrer" : undefined}
                  className="mt-3 block font-serif text-xl sm:text-2xl text-ink hover:text-rust transition-colors duration-300"
                >
                  {d.value}
                </a>
              ) : (
                <span
                  data-testid={d.testid}
                  className="mt-3 block font-serif text-xl sm:text-2xl text-ink"
                >
                  {d.value}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
